/**
 * Etkilesim durumu denetleyicisi.
 *
 * Pointer (canvas) ve kamera hareketini izleyip "etkilesim" durumuna girer/cikar:
 *   - pointer veya kamera etkilesimi baslayinca "interaction-start" yayilir.
 *   - CSS2D (urun olcu metreleri) yalnizca kamera gercekten hareket edince
 *     gizlenir. OrbitControls `start` pointerdown'dadir; CSS'i orada koparmak
 *     metre overlay'ini tiklamada yok eder (resize gibi durur).
 *   - bitince CSS render geri gelir + "interaction-end" yayilir.
 * Pointer dinleyicileri bindElement ile baglanir/cozulur.
 *
 * cssRendererVisible / dispatchCustomEvent / canvas Engine'in public yuzeyinden
 * okunur; ek bagimlilik enjeksiyonu gerekmez.
 */
import type { Engine } from "../Engine";

export class InteractionController {
  private _pointerCount = 0;
  private _cameraActive = false;
  private _interactionActive = false;
  private _pointerListenersAdded = false;

  constructor(private readonly _engine: Engine) {}

  /** camera-change / camera-end event'lerinden cagrilir. */
  setCameraActive(active: boolean): void {
    this._cameraActive = active;
    if (active) {
      this._tryEnter();
      this._hideCssForCamera();
    } else this._tryExit();
  }

  /** bindElement ile: element varsa pointer dinleyicilerini ekle, yoksa cikar. */
  bind(element: HTMLElement | undefined): void {
    const canvas = this._engine.canvas;
    if (!element && this._pointerListenersAdded) {
      canvas.removeEventListener("pointerdown", this._onPointerDown);
      canvas.removeEventListener("pointerup", this._onPointerUp);
      canvas.removeEventListener("pointercancel", this._onPointerUp);
      this._pointerListenersAdded = false;
    }
    if (element && !this._pointerListenersAdded) {
      canvas.addEventListener("pointerdown", this._onPointerDown);
      canvas.addEventListener("pointerup", this._onPointerUp);
      canvas.addEventListener("pointercancel", this._onPointerUp);
      this._pointerListenersAdded = true;
    }
  }

  private _hideCssForCamera(): void {
    if (this._cameraActive && !this._engine.css2dVisibleDuringPointer) {
      this._engine.cssRendererVisible = false;
    }
  }

  private _tryEnter(): void {
    if (this._interactionActive) return;
    if (this._pointerCount > 0 || this._cameraActive) {
      this._interactionActive = true;
      this._hideCssForCamera();
      // Etkilesim sirasinda dusuk pixel ratio denemesi (devre disi birakildi):
      // const pr = Math.max(1, window.devicePixelRatio / 2);
      this._engine.dispatchCustomEvent("interaction-start");
    }
  }

  private _tryExit(): void {
    if (!this._interactionActive) return;
    if (this._pointerCount === 0 && !this._cameraActive) {
      this._interactionActive = false;
      this._engine.cssRendererVisible = true;
      // const pr = Math.max(2, window.devicePixelRatio);
      this._engine.dispatchCustomEvent("interaction-end");
    }
  }

  private _onPointerDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this._pointerCount++;
    this._tryEnter();
  };

  private _onPointerUp = () => {
    this._pointerCount--;
    if (this._pointerCount < 0) this._pointerCount = 0;
    this._tryExit();
  };
}
